/**
 * @file ConnectedAiApps.tsx
 * @brief Profile section: AI apps that can read your meetings, with Disconnect.
 *
 * @details
 * Every grant the user approved on the consent page (Claude, ChatGPT…).
 * Disconnect revokes it server-side: the app's next call is refused, not
 * just its next login. Always shown, with an empty line when nothing is
 * connected, so people know where to look (DES 06-10-2026).
 *
 * @references
 * - BACKEND/src/features/mcp/oauth.routes.mjs (GET/DELETE /api/mcp/connections)
 *
 * @author DES
 * @date 06-10-2026
 */

import { LoaderCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { toast } from "@/shared/hooks/useToast";
import { formatRelative } from "@/shared/utils/formatters";
import { isAutomationKind } from "@/features/integrations/zapierCopy";
import { useAiConnections, useRevokeAiConnection } from "./api";

export function ConnectedAiApps() {
  const list = useAiConnections();
  const revoke = useRevokeAiConnection();
  // Zapier and Make are listed in Settings → Integrations (Z1, M1), not as AI apps.
  const apps = (list.data ?? []).filter((a) => !isAutomationKind(a.kind));

  const onDisconnect = async (id: string, name: string) => {
    try {
      await revoke.mutateAsync(id);
      toast({ title: `${name} disconnected`, description: "It can no longer read your meetings." });
    } catch {
      toast({ title: "Could not disconnect", description: "Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        Connected AI apps
        <span className="ml-1 font-normal text-muted-foreground">
          · These can read your meetings. Disconnect to stop them at once.
        </span>
      </GridBand>
      {apps.length === 0 ? (
        <div className="flex items-center gap-3 px-4 py-3.5 md:px-5">
          <Sparkles className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <span className="text-sm text-muted-foreground">
            {list.isLoading
              ? "Checking…"
              : list.isError
                ? "Could not load connected apps."
                : "No AI apps connected. Connect Claude or ChatGPT from inside that app."}
          </span>
        </div>
      ) : null}
      <ul>
        {apps.map((a) => {
          const busy = revoke.isPending && revoke.variables === a.id;
          return (
            <li
              key={a.id}
              className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5 last:border-b-0 md:px-5"
            >
              <Sparkles className="h-5 w-5 shrink-0 text-brand" strokeWidth={1.75} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {a.appName}
                  {a.appHost && a.appHost !== a.appName ? (
                    <span className="ml-1.5 font-normal text-muted-foreground">{a.appHost}</span>
                  ) : null}
                </span>
                <span className="block text-xs text-muted-foreground">
                  Connected {formatRelative(a.connectedAt)}
                  {a.lastUsedAt ? ` · last used ${formatRelative(a.lastUsedAt)}` : ""}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                disabled={busy}
                onClick={() => void onDisconnect(a.id, a.appName)}
              >
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Disconnect"}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
